<script>
import { mapGetters } from 'vuex';
import AgentTable from './components/overview/AgentTable.vue';
import DepartmentTable from './components/overview/DepartmentTable.vue';
import MetricCard from './components/overview/MetricCard.vue';
import { OVERVIEW_METRICS } from './constants';
import ReportHeatmap from './components/Heatmap.vue';
import AgentOnlineHeatmap from './components/AgentOnlineHeatmap.vue';
import SvgIcon from 'dashboard/components-next/SvgIcon/index.vue';
import ReportsFiltersTeams from './components/Filters/Teams.vue';
import ReportsFiltersAgents from './components/Filters/Agents.vue';

import endOfDay from 'date-fns/endOfDay';
import getUnixTime from 'date-fns/getUnixTime';
import startOfDay from 'date-fns/startOfDay';
import subDays from 'date-fns/subDays';
import ReportHeader from './components/ReportHeader.vue';
import { format } from 'date-fns';
import InboxTable from './components/overview/InboxTable.vue';

export const FETCH_INTERVAL = 60000;

// 自动刷新配置
export const AUTO_REFRESH_CONFIG = {
  accountConversationMetric: true,
  agentConversationMetric: false,
  inboxConversationMetric: true,
  agentOnlineHeatmap: true,
  accountConversationHeatmap: true,
  departmentConversationMetric: true,
};

export default {
  name: 'LiveReports',
  components: {
    ReportHeader,
    AgentTable,
    DepartmentTable,
    InboxTable,
    MetricCard,
    ReportHeatmap,
    AgentOnlineHeatmap,
    ReportsFiltersTeams,
    SvgIcon,
    ReportsFiltersAgents,
  },
  data() {
    return {
      // always start with 0, this is to manage the pagination in tanstack table
      // when we send the data, we do a +1 to this value
      agentPageIndex: 0,
      inboxPageIndex: 0,
      departmentPageIndex: 0,
      agentOnlineHeatmapDate: null,
      // 筛选器数据 - 使用 namespace 区分不同看板
      agentConversationFilters: {
        selectedTeams: [],
        selectedAgents: [],
        startDate: null,
        endDate: null,
      },
      // 筛选器数据 - 使用 namespace 区分不同看板
      departmentFilters: {
        selectedTeams: [],
      },
      agentOnlineHeatmapFilters: {
        selectedTeams: [],
        selectedAgents: [],
      },
    };
  },
  computed: {
    todayDate() {
      return format(new Date(), 'yyyy-MM-dd');
    },
    ...mapGetters({
      agentStatus: 'agents/getAgentStatus',
      agents: 'agents/getAgents',
      accountConversationMetric: 'getAccountConversationMetric',
      agentConversationMetric: 'getAgentConversationMetric',
      agentConversationMetricTotalCount: 'getAgentConversationMetricTotalCount',
      inboxConversationMetric: 'getInboxConversationMetric',
      inboxConversationMetricTotalCount: 'getInboxConversationMetricTotalCount',
      departmentConversationMetric: 'getDepartmentConversationMetric',
      departmentConversationMetricTotalCount: 'getDepartmentConversationMetricTotalCount',
      accountConversationHeatmap: 'getAccountConversationHeatmapData',
      uiFlags: 'getOverviewUIFlags',
      agentOnlineHeatmapData: 'getAgentOnlineHeatmapData',
      accountId: 'getCurrentAccountId',
    }),
    agentStatusMetrics() {
      let metric = {};
      Object.keys(this.agentStatus).forEach(key => {
        const metricName = this.$t(
          `OVERVIEW_REPORTS.AGENT_STATUS.${OVERVIEW_METRICS[key]}`
        );
        metric[metricName] = this.agentStatus[key];
      });
      return metric;
    },
    conversationMetrics() {
      let metric = {};
      Object.keys(this.accountConversationMetric).forEach(key => {
        const metricName = this.$t(
          `OVERVIEW_REPORTS.ACCOUNT_CONVERSATIONS.${OVERVIEW_METRICS[key]}`
        );
        metric[metricName] = this.accountConversationMetric[key];
      });
      return metric;
    },
  },
  mounted() {
    this.$store.dispatch('agents/get');
    this.initalizeReport();
  },
  beforeUnmount() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
  },
  watch: {
    // 监听日期选择变化，动态修改自动刷新配置
    agentOnlineHeatmapDate(newValue, oldValue) {
      // 避免初始化时的无效调用
      if (newValue !== oldValue) {
        // 当选择了日期时，禁用 agentOnlineHeatmap 的自动刷新
        AUTO_REFRESH_CONFIG.agentOnlineHeatmap = !newValue;
        this.fetchAgentOnlineHeatmap();
      }
    },
  },
  methods: {
    initalizeReport() {
      this.fetchAllData(true);
      this.scheduleReportRefresh();
    },
    scheduleReportRefresh() {
      this.timeoutId = setTimeout(async () => {
        await this.fetchAllData();
        this.scheduleReportRefresh();
      }, FETCH_INTERVAL);
    },
    fetchAllData(isInit) {
      if (AUTO_REFRESH_CONFIG.accountConversationMetric || isInit) {
        this.fetchAccountConversationMetric();
      }
      if (AUTO_REFRESH_CONFIG.agentConversationMetric || isInit) {
        this.fetchAgentConversationMetric();
      }
      if (AUTO_REFRESH_CONFIG.inboxConversationMetric || isInit) {
        this.fetchInboxConversationMetric();
      }
      if (AUTO_REFRESH_CONFIG.agentOnlineHeatmap || isInit) {
        this.fetchAgentOnlineHeatmap();
      }
      if (AUTO_REFRESH_CONFIG.accountConversationHeatmap || isInit) {
        this.fetchHeatmapData();
      }
      if (AUTO_REFRESH_CONFIG.departmentConversationMetric || isInit) {
        this.fetchDepartmentConversationMetric();
      }
    },
    downloadHeatmapData() {
      let to = endOfDay(new Date());

      this.$store.dispatch('downloadAccountConversationHeatmap', {
        to: getUnixTime(to),
      });
    },
    fetchHeatmapData() {
      if (this.uiFlags.isFetchingAccountConversationsHeatmap) {
        return;
      }

      // the data for the last 6 days won't ever change,
      // so there's no need to fetch it again
      // but we can write some logic to check if the data is already there
      // if it is there, we can refetch data only for today all over again
      // and reconcile it with the rest of the data
      // this will reduce the load on the server doing number crunching
      let to = endOfDay(new Date());
      let from = startOfDay(subDays(to, 6));

      if (this.accountConversationHeatmap.length) {
        to = endOfDay(new Date());
        from = startOfDay(to);
      }

      this.$store.dispatch('fetchAccountConversationHeatmap', {
        metric: 'conversations_count',
        from: getUnixTime(from),
        to: getUnixTime(to),
        groupBy: 'hour',
        businessHours: false,
      });
    },
    fetchAgentOnlineHeatmap() {
      // 未选择日期时，使用当前日期
      const date = this.agentOnlineHeatmapDate || this.todayDate;
      
      // 构建筛选参数
      const filterParams = this.buildAgentOnlineHeatmapFilterParams();
      
      const requestParams = {
        inputMillis: new Date(date).valueOf(),
        accountId: this.accountId,
        ...filterParams, // 展开筛选参数
      };
      
      this.$store.dispatch('fetchAgentOnlineHeatmap', requestParams);
    },
    fetchAccountConversationMetric() {
      this.$store.dispatch('fetchAccountConversationMetric', {
        type: 'account',
      });
    },
    async fetchAgentConversationMetric() {
      // 构建筛选参数
      const filterParams = this.buildAgentConversationFilterParams('agentConversationFilters');
      
      // 调试日志
      console.log('Agent Conversation 筛选参数:', filterParams);
      
      const requestParams = {
        pageNo: this.agentPageIndex + 1,
        accountId: this.accountId,
        ...filterParams, // 展开筛选参数
      };
      
      console.log('发送请求参数:', requestParams);
      
      await this.$store.dispatch('fetchAgentConversationMetric', requestParams);
    },
    async onAgentPageNumberChange(agentPageIndex) {
      this.agentPageIndex = agentPageIndex;
      await this.fetchAgentConversationMetric();
      document.querySelector('.reports--wrapper').scrollTop = 1189;
    },
    fetchInboxConversationMetric() {
      this.$store.dispatch('fetchInboxConversationMetric', {
        pageNo: this.inboxPageIndex + 1,
        accountId: this.accountId,
      });
    },
    onInboxPageNumberChange(inboxPageIndex) {
      this.inboxPageIndex = inboxPageIndex;
      this.fetchInboxConversationMetric();
    },
    async fetchDepartmentConversationMetric() {
      // 构建筛选参数
      const filterParams = this.buildDepartmentFilterParams();
      
      const requestParams = {
        pageNo: this.departmentPageIndex + 1,
        accountId: this.accountId,
        ...filterParams, // 展开筛选参数
      };
      
      await this.$store.dispatch('fetchDepartmentConversationMetric', requestParams);
    },
    onDepartmentPageNumberChange(departmentPageIndex) {
      this.departmentPageIndex = departmentPageIndex;
      this.fetchDepartmentConversationMetric();
    },
    // 筛选器处理方法
    handleDepartmentTeamFilterSelection(selectedTeams) {
      this.departmentFilters.selectedTeams = selectedTeams;
      this.applyDepartmentFilters();
    },
    // Agent Online Heatmap 筛选器处理方法
    handleAgentOnlineHeatmapTeamFilterSelection(selectedTeams) {
      this.agentOnlineHeatmapFilters.selectedTeams = selectedTeams;
      this.applyAgentOnlineHeatmapFilters();
    },
    handleAgentOnlineHeatmapAgentsFilterSelection(selectedAgents) {
      this.agentOnlineHeatmapFilters.selectedAgents = selectedAgents;
      this.applyAgentOnlineHeatmapFilters();
    },
    // 筛选器处理方法
    handleTeamFilterSelection(selectedTeams) {
      this.agentConversationFilters.selectedTeams = selectedTeams;
      this.applyAgentConversationFilters();
    },
    handleAgentsFilterSelection(selectedAgents) {
      this.agentConversationFilters.selectedAgents = selectedAgents;
      this.applyAgentConversationFilters();
    },
    processDateTimeValue(dateTimeValue) {
      if (dateTimeValue) {
        const [datePart, timePart] = dateTimeValue.split('T');
        const [hour] = timePart.split(':');
        return `${datePart}T${hour.padStart(2, '0')}:00`;
      }
      return dateTimeValue;
    },
    handleStartDateChange(event) {
      this.agentConversationFilters.startDate = this.processDateTimeValue(event.target.value);
      this.applyAgentConversationFilters();
    },
    handleEndDateChange(event) {
      this.agentConversationFilters.endDate = this.processDateTimeValue(event.target.value);
      this.applyAgentConversationFilters();
    },
    handleDateInputClick(event) {
      // 强制触发日期选择器的显示
      event.target.showPicker && event.target.showPicker();
    },
    applyDepartmentFilters() {
      // 重新获取数据
      this.fetchDepartmentConversationMetric();
    },
    applyAgentOnlineHeatmapFilters() {
      // 重新获取数据
      this.fetchAgentOnlineHeatmap();
    },
    applyAgentConversationFilters() {
      // 重新获取数据
      this.fetchAgentConversationMetric();
    },
    buildDepartmentFilterParams() {
      const filters = this.departmentFilters;
      const params = {};
      
      // 处理 teamIds - 支持多选，传递数组
      if (filters.selectedTeams && filters.selectedTeams.length > 0) {
        params.teamIds = filters.selectedTeams.map(team => team.id) ?? [];
      }
      
      return params;
    },
    buildAgentOnlineHeatmapFilterParams() {
      const filters = this.agentOnlineHeatmapFilters;
      const params = {};
      
      // 处理 teamIds - 支持多选，传递数组
      if (filters.selectedTeams && filters.selectedTeams.length > 0) {
        params.teamIds = filters.selectedTeams.map(team => team.id);
      }
      
      // 处理 agentIds - 支持多选，传递数组
      if (filters.selectedAgents && filters.selectedAgents.length > 0) {
        params.agentIds = filters.selectedAgents.map(agent => agent.id);
      }
      
      return params;
    },
    buildAgentConversationFilterParams(namespace = 'agentConversationFilters') {
      const filters = this[namespace];
      const params = {};
      
      // 处理 teamIds - 支持多选，传递数组
      if (filters.selectedTeams && filters.selectedTeams.length > 0) {
        params.teamIds = filters.selectedTeams.map(team => team.id);
      }
      
      // 处理 agentIds - 支持多选，传递数组
      if (filters.selectedAgents && filters.selectedAgents.length > 0) {
        params.agentIds = filters.selectedAgents.map(agent => agent.id);
      }
      
      // 处理日期 - 转换为 UTC 毫秒级时间戳
      if (filters.startDate) {
        // 开始日期转换为 UTC 毫秒级时间戳
        params.startDate = new Date(filters.startDate).valueOf();
      }
      
      if (filters.endDate) {
        // 结束日期转换为 UTC 毫秒级时间戳
        params.endDate = new Date(filters.endDate).valueOf();
      }
      
      return params;
    },
  },
};
</script>

<template>
  <ReportHeader :header-title="$t('OVERVIEW_REPORTS.HEADER')" />
  <div class="flex flex-col gap-4 pb-2">
    <div class="flex flex-col items-center md:flex-row gap-4">
      <div
        class="flex-1 w-full max-w-full md:w-[65%] md:max-w-[65%] conversation-metric"
      >
        <MetricCard
          :header="$t('OVERVIEW_REPORTS.ACCOUNT_CONVERSATIONS.HEADER')"
          :is-loading="uiFlags.isFetchingAccountConversationMetric"
          :loading-message="
            $t('OVERVIEW_REPORTS.ACCOUNT_CONVERSATIONS.LOADING_MESSAGE')
          "
        >
          <div
            v-for="(metric, name, index) in conversationMetrics"
            :key="index"
            class="flex-1 min-w-0 pb-2"
          >
            <h3 class="text-base text-n-slate-11">
              {{ name }}
            </h3>
            <p class="text-n-slate-12 text-3xl mb-0 mt-1">
              {{ metric }}
            </p>
          </div>
        </MetricCard>
      </div>
      <div class="flex-1 w-full max-w-full md:w-[35%] md:max-w-[35%]">
        <MetricCard :header="$t('OVERVIEW_REPORTS.AGENT_STATUS.HEADER')">
          <div
            v-for="(metric, name, index) in agentStatusMetrics"
            :key="index"
            class="flex-1 min-w-0 pb-2"
          >
            <h3 class="text-base text-n-slate-11">
              {{ name }}
            </h3>
            <p class="text-n-slate-12 text-3xl mb-0 mt-1">
              {{ metric }}
            </p>
          </div>
        </MetricCard>
      </div>
    </div>
    <div class="flex flex-row flex-wrap max-w-full">
      <MetricCard :header="$t('OVERVIEW_REPORTS.CONVERSATION_HEATMAP.HEADER')">
        <template #control>
          <woot-button
            icon="arrow-download"
            size="small"
            variant="smooth"
            color-scheme="secondary"
            @click="downloadHeatmapData"
          >
            {{ $t('OVERVIEW_REPORTS.CONVERSATION_HEATMAP.DOWNLOAD_REPORT') }}
          </woot-button>
        </template>
        <ReportHeatmap
          :heat-data="accountConversationHeatmap"
          :is-loading="uiFlags.isFetchingAccountConversationsHeatmap"
        />
      </MetricCard>
    </div>
    <div class="flex flex-row flex-wrap max-w-full">
      <MetricCard :header="'Agent Status'" class="agent-status-heatmap">
        <template #control>
          <input
            min="2025-06-06"
            :max="todayDate"
            v-model="agentOnlineHeatmapDate"
            type="date"
            step="1"
            @click="handleDateInputClick"
            class="py-1.5 px-3 text-n-slate-12 bg-n-alpha-1 text-sm rounded-lg reset-base cursor-pointer"
            :placeholder="$t('FILTER.INPUT_PLACEHOLDER')"
            style="pointer-events: auto;"
          />
        </template>
        <template #filter>
          <div class="flex gap-3 items-center">
            <div class="flex items-center gap-2 w-1/2">
              <label class="text-sm text-n-slate-11 whitespace-nowrap">Team:</label>
              <ReportsFiltersTeams 
                :style="{ flex: 1 }"
                @team-filter-selection="handleAgentOnlineHeatmapTeamFilterSelection" 
              />
            </div>
            <div class="flex items-center gap-2 w-1/2">
              <label class="text-sm text-n-slate-11 whitespace-nowrap">Agent:</label>
              <ReportsFiltersAgents 
                :style="{ flex: 1 }"
                @agents-filter-selection="handleAgentOnlineHeatmapAgentsFilterSelection" 
              />
            </div>
          </div>
        </template>
        <AgentOnlineHeatmap
          :agent-data="agentOnlineHeatmapData"
          :is-loading="uiFlags.isFetchingAgentOnlineHeatmap"
          :agents="agents"
        />
      </MetricCard>
    </div>
    
    <div class="flex flex-row flex-wrap max-w-full">
      <MetricCard 
        header="Team Performance"
        :is-loading="uiFlags.isFetchingDepartmentConversationMetric"
        :loading-message="'Loading team data...'"
      >
        <template #filter>
          <div class="flex flex-wrap gap-3 items-center">
            <div class="flex items-center gap-2 w-full">
              <label class="text-sm text-n-slate-11 whitespace-nowrap">Team:</label>
              <ReportsFiltersTeams 
                :enable-scroll="true"
                max-height="145px"
                min-width="30%"
                @team-filter-selection="handleDepartmentTeamFilterSelection" 
              />
            </div>
          </div>
        </template>
        <DepartmentTable
          :department-total-count="departmentConversationMetricTotalCount"
          :department-metrics="departmentConversationMetric"
          :page-index="departmentPageIndex"
          :is-loading="uiFlags.isFetchingDepartmentConversationMetric"
          @page-change="onDepartmentPageNumberChange"
        />
      </MetricCard>
    </div>
    
    <div class="flex flex-row flex-wrap max-w-full">
      <MetricCard 
        :header="$t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.HEADER')"
        :is-loading="uiFlags.isFetchingAgentConversationMetric"
        :loading-message="$t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.LOADING_MESSAGE')"
      >
        <template #control>
          <button
            class="px-3 py-1.5 text-sm text-n-slate-12 bg-n-slate-4 rounded transition-colors flex items-center justify-center"
            :disabled="uiFlags.isFetchingAgentConversationMetric"
            @click="fetchAgentConversationMetric"
          >
            <SvgIcon name="refresh" size="12" class="mr-2 flex-shrink-0" />
            Refresh
          </button>
        </template>
        <template #filter>
          <div class="flex flex-col gap-3">
            <!-- 第一行：Team 和 Agent -->
            <div class="flex gap-3 items-center">
              <div class="flex items-center gap-2 w-1/2">
                <label class="text-sm text-n-slate-11 whitespace-nowrap">Team:</label>
                <ReportsFiltersTeams 
                  :style="{ flex: 1 }"
                  @team-filter-selection="handleTeamFilterSelection" 
                />
              </div>
              <div class="flex items-center gap-2 w-1/2">
                <label class="text-sm text-n-slate-11 whitespace-nowrap">Agent:</label>
                <ReportsFiltersAgents 
                  :style="{ flex: 1 }"
                  @agents-filter-selection="handleAgentsFilterSelection" 
                />
              </div>
            </div>
            <!-- 第二行：StartTime 和 EndTime -->
            <div class="flex gap-3 items-center">
              <div class="flex items-center gap-2 w-1/2">
                <label class="text-sm text-n-slate-11 whitespace-nowrap">StartTime:</label>
                <div class="multiselect-wrap--small flex-1">
                  <input
                    type="datetime-local"
                    step="3600"
                    :value="agentConversationFilters.startDate"
                    @change="handleStartDateChange"
                    @click="handleDateInputClick"
                    class="reset-base w-full px-3 text-sm border border-solid border-slate-200 dark:border-slate-600 rounded-[5px] bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 h-[2.5rem] min-h-[2.5rem] cursor-pointer"
                    style="pointer-events: auto;"
                  />
                </div>
              </div>
              <div class="flex items-center gap-2 w-1/2">
                <label class="text-sm text-n-slate-11 whitespace-nowrap">EndTime:</label>
                <div class="multiselect-wrap--small flex-1">
                  <input
                    type="datetime-local"
                    step="3600"
                    :value="agentConversationFilters.endDate"
                    @change="handleEndDateChange"
                    @click="handleDateInputClick"
                    class="reset-base w-full px-3 text-sm border border-solid border-slate-200 dark:border-slate-600 rounded-[5px] bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 h-[2.5rem] min-h-[2.5rem] cursor-pointer"
                    style="pointer-events: auto;"
                  />
                </div>
              </div>
            </div>
          </div>
        </template>
        <AgentTable
          :agents="agents"
          :agent-total-count="agentConversationMetricTotalCount"
          :agent-metrics="agentConversationMetric"
          :page-index="agentPageIndex"
          :is-loading="uiFlags.isFetchingAgentConversationMetric"
          @page-change="onAgentPageNumberChange"
        />
      </MetricCard>
    </div>
    <div class="flex flex-row flex-wrap max-w-full">
      <MetricCard :header="$t('OVERVIEW_REPORTS.INBOX_CONVERSATIONS.HEADER')">
        <InboxTable
          :inbox-total-count="inboxConversationMetricTotalCount"
          :inbox-metrics="inboxConversationMetric"
          :page-index="inboxPageIndex"
          :is-loading="uiFlags.isFetchingInboxConversationMetric"
          @page-change="onInboxPageNumberChange"
        />
      </MetricCard>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.agent-status-heatmap :deep(.card-header div) {
  opacity: 1;
}
</style>
